<template>
 <v-dialog
   v-model="dialog"
   width="95%"
   max-width="680px"
   persistent
   scrollable
   transition="dialog-transition"
 >
   <v-card class="dialog-card modern-card">
     <!-- Enhanced Header with Gradient -->
     <v-card-title class="dialog-header">
       <div class="header-content">
         <div class="header-main">
           <div class="header-icon-wrapper">
             <v-icon size="24" class="header-icon" color="#fff">mdi-calendar-plus</v-icon>
           </div>
           <div class="header-text">
             <h2 class="dialog-title">Create New Event</h2>
             <p class="dialog-subtitle">Share your moment with the community</p>
           </div>
         </div>
         <v-btn
           icon
           @click="onCloseDialog"
           class="close-btn modern-close"
           elevation="0"
         >
           <v-icon color="#fff">mdi-close</v-icon>
         </v-btn>
       </div>
     </v-card-title>

     <v-card-text class="dialog-body modern-body">
       <!-- Enhanced User Profile Section -->
       <div class="user-profile-section">
         <div class="user-profile-card">
           <div class="avatar-wrapper">
             <editable-avatar
               :image="profile.thumbnail || profile.image"
               :editable="false"
               :size="56"
               class="user-avatar"
             />
             <div class="online-indicator"></div>
           </div>
           <div class="user-info">
             <h3 class="user-name">
                 {{ truncateName(profile.name) || truncateFullName(profile.first_name, profile.last_name) }}
             </h3>
             <p class="user-status">Ready to share</p>
           </div>
         </div>
       </div>

       <!-- Modern Post Type Selection -->
       <div class="post-type-section">
         <div class="section-header">
           <h4 class="section-title">Choose Post Type</h4>
           <p class="section-description">Select how you want to share your content</p>
         </div>
        
         <div class="post-type-grid">
           <div
             v-for="option in filteredPostTypeOptions"
             :key="option.value"
             class="post-type-card"
             :class="{
               'active': selectedPostType === option.value,
               'animate-in': true
             }"
             @click="selectedPostType = option.value"
           >
             <div class="card-background"></div>
             <div class="card-content">
               <div class="icon-container">
                 <v-icon class="type-icon" size="28">{{ option.icon }}</v-icon>
               </div>
               <div class="type-info">
                 <h5 class="type-title">{{ option.label }}</h5>
                 <p class="type-description">
                   {{ option.value === 'Competition' ? 'Join active competitions' : 'Share personal updates' }}
                 </p>
               </div>
             </div>
             <div class="selection-indicator">
               <v-icon class="check-icon">mdi-check-circle</v-icon>
             </div>
           </div>
         </div>

         <!-- Enhanced Competition Dropdown -->
         <transition name="fade-slide" mode="out-in">
           <div v-if="selectedPostType === 'Competition' && hasCompetitions" class="competition-selector mb-5">
             <v-select
               v-model="selectedCompetition"
               :items="competitionOptions"
               item-text="label"
               item-value="value"
               label="Select Competition"
               outlined
               dense
               class="modern-select"
               hide-details
               prepend-inner-icon="mdi-trophy"
             >
               <template v-slot:selection="{ item }">
                 <div class="selection-content">
                   {{ item.label }}
                 </div>
               </template>
             </v-select>
           </div>
         </transition>

         <!-- Show message when no competitions available -->
         <transition name="fade-slide" mode="out-in">
           <div v-if="selectedPostType === 'Competition' && !hasCompetitions && competitionsLoaded" class="no-competitions-message">
             <v-alert
               type="info"
               outlined
               dense
               class="modern-alert"
             >
               <v-icon left>mdi-information</v-icon>
               No active competitions available at the moment. Please check back later.
             </v-alert>
           </div>
         </transition>
       </div>

       <!-- Enhanced Content Section -->
       <div class="content-section">
         <div class="content-wrapper">
           <v-textarea
             v-model="text"
             ref="textarea"
             outlined
             rows="4"
             auto-grow
             label="What's on your mind?"
             placeholder="Share your thoughts, experiences, or updates..."
             hide-details
             class="modern-textarea"
             @focus="emojiKeyboard = false"
           >
             <template v-slot:prepend-inner>
               <v-icon class="content-icon">mdi-pencil</v-icon>
             </template>
           </v-textarea>
           
           <!-- Character Counter -->
           <div class="character-counter" v-if="text.length > 0">
             <span :class="{ 'text-error': text.length < 20 }">
               {{ text.length }} / 20 minimum
             </span>
             <span v-if="text.length > 0 && text.length < 20" class="min-char-warning">
               <v-icon x-small color="warning">mdi-alert</v-icon>
               Minimum 20 characters required
             </span>
           </div>
         </div>
       </div>

       <!-- Enhanced Media Section -->
       <transition name="media-transition" mode="out-in">
         <div v-if="selectedFiles.length > 0" class="media-section">
           <div class="media-header">
             <h4 class="media-title">
               <v-icon class="mr-2">mdi-image-multiple</v-icon>
               Media Attachments ({{ selectedFiles.length }})
             </h4>
             <div v-if="selectedFiles.length > 1" class="drag-hint">
               <v-icon small class="mr-1">mdi-drag</v-icon>
               Drag to reorder
             </div>
           </div>

           <draggable
             v-model="selectedFiles"
             group="files"
             @end="onDragEnd"
             class="media-grid modern-grid"
             :class="{
               'single-item': selectedFiles.length === 1,
               'multiple-items': selectedFiles.length > 1
             }"
           >
             <div
               v-for="(file, index) in selectedFiles"
               :key="file.url + index"
               class="media-item modern-media"
             >
               <div class="media-preview">
                 <v-img
                   v-if="file.type === 'image'"
                   :src="file.url"
                   contain
                   class="media-image"
                   @click="openMediaViewer(file)"
                 >
                   <template v-slot:placeholder>
                     <div class="image-placeholder">
                       <v-icon size="48">mdi-image</v-icon>
                     </div>
                   </template>
                 </v-img>
                 <div v-else-if="file.type === 'video'" class="video-container">
                   <video controls class="media-video" @click="openMediaViewer(file)">
                     <source :src="file.url" type="video/mp4" />
                   </video>
                   <div class="video-overlay">
                     <v-icon class="play-icon">mdi-play-circle</v-icon>
                   </div>
                   <div class="video-duration" v-if="file.duration">
                     {{ formatDuration(file.duration) }}
                   </div>
                 </div>
               </div>
               <v-btn
                 @click.stop="onRemoveAttachment(file)"
                 icon
                 small
                 class="remove-btn modern-remove"
                 elevation="2"
               >
                 <v-icon small>mdi-close</v-icon>
               </v-btn>
               <div class="media-overlay">
                 <v-icon class="expand-icon">mdi-fullscreen</v-icon>
               </div>
             </div>
           </draggable>
         </div>
       </transition>
     </v-card-text>

     <!-- Enhanced Action Bar -->
     <v-card-actions class="dialog-actions modern-actions">
       <div class="action-group">
         <v-tooltip top>
           <template v-slot:activator="{ on, attrs }">
             <v-btn
               icon
               large
               @click="onMediaPick"
               :loading="imageLoading"
               v-bind="attrs"
               v-on="on"
               class="action-btn media-btn"
               elevation="0"
             >
               <v-icon>mdi-image-plus</v-icon>
             </v-btn>
           </template>
           <span>Add photos or videos</span>
         </v-tooltip>

        <v-menu
          v-model="emojiKeyboard"
          bottom
          offset-y
          :close-on-content-click="false"
          transition="scale-transition"
          @input="onEmojiMenuToggle"
        >
          <template v-slot:activator="{ on: menu }">
            <v-tooltip top>
              <template v-slot:activator="{ on: tooltip }">
                <v-btn
                  icon
                  large
                  v-on="{ ...tooltip, ...menu }"
                  class="action-btn emoji-btn"
                  elevation="0"
                >
                  <v-icon>mdi-emoticon-happy</v-icon>
                </v-btn>
              </template>
              <span>Add emoji</span>
            </v-tooltip>
          </template>
          <v-card class="emoji-menu" elevation="8">
            <VEmojiPicker 
              @select="selectEmoji"
              class="emoji-picker"
            />
          </v-card>
        </v-menu>
       </div>

       <v-spacer></v-spacer>

       <v-btn
         color="primary"
         @click="onPost"
         :loading="loading"
         :disabled="!isPostValid"
         large
         class="post-btn modern-post-btn"
         elevation="2"
       >
         <span class="btn-text">Publish</span>
         <v-icon right class="send-icon">mdi-send</v-icon>
       </v-btn>
     </v-card-actions>

     <!-- Enhanced Error Notification -->
     <v-snackbar
       v-model="snackbar"
       :timeout="4000"
       color="error"
       top
       class="modern-snackbar"
       elevation="6"
     >
       <div class="snackbar-content">
         <v-icon class="mr-3">mdi-alert-circle</v-icon>
         {{ errorMessage }}
       </div>
       <template v-slot:action="{ attrs }">
         <v-btn
           text
           v-bind="attrs"
           @click="snackbar = false"
           class="snackbar-action"
         >
           Dismiss
         </v-btn>
       </template>
     </v-snackbar>
   </v-card>
 </v-dialog>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import EditableAvatar from "@/components/EditableAvatar.vue";
import draggable from "vuedraggable";

export default {
  components: { EditableAvatar, draggable },
  props: {
    dialog: { type: Boolean, default: false },
    onCloseDialog: { type: Function, default: () => {} },
    initalType: { type: String, default: "" }
  },
  data() {
    return {
      loading: false,
      selectedFiles: [],
      text: "",
      emojiKeyboard: false,
      errorMessage: null,
      snackbar: false,
      imageLoading: false,
      selectedPostType: "Individual",
      showRadios: false,
      volntrRadios: false,
      selectedCompetition: null,
      competitions: [],
      competitionsLoaded: false,
      hasCompetitions: false,
      // Minimum character requirements
      minCharacters: 20,
      postTypeOptions: [
        { label: "Competition", value: "Competition", icon: "mdi-trophy", show: () => this.showCompetitionOption },
        { label: "Individual", value: "Individual", icon: "mdi-account", show: () => true }
      ]
    };
  },
  computed: {
    ...mapState("auth", { profile: "profile" }),
    ...mapGetters("event", { getActiveChallenges: "getActiveChallenges" }),

    showCompetitionOption() {
      if (!this.hasCompetitions) return false;
      
      if (this.profile.type === "Agency") return this.showRadios;
      if (["Volunteer", "Company"].includes(this.profile.type)) return this.volntrRadios;
      return false;
    },

    // Enhanced validation with minimum characters
    isPostValid() {
      if (!this.selectedPostType) return false;
      
      const hasContent = this.text.trim().length >= this.minCharacters || this.selectedFiles.length > 0;
      
      if (this.selectedPostType === "Competition") {
        return hasContent && this.selectedCompetition && this.hasCompetitions;
      }
      return hasContent && this.selectedPostType;
    },

    filteredPostTypeOptions() {
      let options = [];
      
      if (this.profile.type === "Agency" && this.showRadios && this.hasCompetitions) {
        options = this.postTypeOptions;
      } else if (["Volunteer", "Company"].includes(this.profile.type) && this.volntrRadios && this.hasCompetitions) {
        options = this.postTypeOptions;
      } else {
        options = this.postTypeOptions.filter(option => option.value === "Individual");
      }
      
      if (!this.hasCompetitions) {
        options = options.filter(option => option.value !== "Competition");
      }
      
      return options;
    },

    competitionOptions() {
      const competitions = this.competitions.length > 0 ? this.competitions : this.getActiveChallenges;
      
      if (!competitions || competitions.length === 0) {
        return [];
      }
      
      return competitions.map(item => {
        const competition = item.competitionId || item;
        return {
          label: this.sanitizeString(competition?.title || competition?.name || 'Untitled Competition'),
          value: competition?._id || item._id || item.id
        };
      });
    }
  },
  methods: {
    ...mapActions({
      addEvent: "event/addEvent",
      activeCompentition: "event/getCompetitionActive",
      fetchMyActiveChallenges: "event/fetchMyActiveChallenges"
    }),

    onEmojiMenuToggle(value) {
      console.log('Emoji menu toggled:', value);
    },

    sanitizeString(str = "") {
      if (!str) return "";
      return String(str).replace(/[<>]/g, "").replace(/["']/g, "");
    },

    truncateName(name, length = 60) {
      if (!name) return "";
      name = this.sanitizeString(name);
      return name.length > length ? name.slice(0, length) + '...' : name;
    },

    truncateFullName(first, last, length = 60) {
      if (!first && !last) return "";
      const fullName = this.sanitizeString(`${first || ''} ${last || ''}`.trim());
      return fullName.length > length ? fullName.slice(0, length) + '...' : fullName;
    },

    selectEmoji(emoji) {
      this.insertText(this.sanitizeString(emoji.data));
      this.$nextTick(() => {
        this.$refs.textarea.focus();
        this.emojiKeyboard = true;
      });
    },

    insertText(text) {
      const el = this.$refs.textarea.$refs.input;
      const cursorPos = el.selectionEnd;
      this.text = `${this.text.substring(0, cursorPos)}${text}${this.text.substring(cursorPos)}`;
      this.$nextTick(() => el.setSelectionRange(cursorPos + text.length, cursorPos + text.length));
    },

    async onMediaPick() {
      this.imageLoading = true;
      try {
        await new Promise(resolve => {
          const input = document.createElement("input");
          input.type = "file";
          input.multiple = true;
          input.accept = "image/*,video/*";

          input.onchange = async e => {
            if (e.target.files.length > 0) await this.onFileChanged(e);
            resolve();
          };
          input.oncancel = () => resolve();
          input.click();
        });
      } finally {
        this.imageLoading = false;
      }
    },

    async onFileChanged(e) {
      const newFiles = await Promise.all(
        Array.from(e.target.files).map(async file => {
          const type = this.getType(file);
          const duration = type === "video" ? await this.getVideoDuration(file) : null;
          return { type, file, url: URL.createObjectURL(file), duration };
        })
      );
      this.selectedFiles = [...this.selectedFiles, ...newFiles];
      this.scrollToBottom();
    },

    onRemoveAttachment(file) {
      this.selectedFiles = this.selectedFiles.filter(f => f !== file);
      URL.revokeObjectURL(file.url);
    },

    async onPost() {
      if (!this.isPostValid) {
        let errorMsg = "Please provide content for your post.";
        
        if (!this.text.trim() && this.selectedFiles.length === 0) {
          errorMsg = "Please add some content or media to your post.";
        } else if (this.text.trim().length > 0 && this.text.trim().length < this.minCharacters) {
          errorMsg = `Please write at least ${this.minCharacters} characters. Current: ${this.text.trim().length} characters.`;
        }
        
        this.showErrorMessage(errorMsg);
        return;
      }

      const formData = new FormData();
      this.selectedFiles.forEach(file => formData.append("files[]", file.file));
      if (this.text) formData.append("text", this.sanitizeString(this.text.trim()));
      formData.append("category", this.sanitizeString(this.selectedPostType));

      if (this.selectedPostType === "Competition" && this.selectedCompetition) {
        formData.append("competitionId", this.selectedCompetition);
      }

      this.loading = true;
      try {
        await this.addEvent(formData);
        this.resetForm();
        this.onCloseDialog();
      } catch (error) {
        this.showErrorMessage(error.response?.data?.message || "Failed to create post. Please try again.");
      } finally {
        this.loading = false;
      }
    },

    getType(file) {
      return file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : "unknown";
    },

    async getVideoDuration(file) {
      return new Promise(resolve => {
        const video = document.createElement("video");
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          resolve(video.duration || 0);
        };
        video.onerror = () => resolve(0);
        video.src = URL.createObjectURL(file);
      });
    },

    formatDuration(seconds) {
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
    },

    showErrorMessage(message) {
      this.errorMessage = message;
      this.snackbar = true;
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.dialogContent;
        if (container) container.scrollTop = container.scrollHeight;
      });
    },

    resetForm() {
      this.selectedFiles.forEach(file => URL.revokeObjectURL(file.url));
      this.selectedFiles = [];
      this.text = "";
      this.selectedPostType = "Individual";
      this.selectedCompetition = null;
    },

    openMediaViewer(file) {
      console.log("Opening media viewer for", file);
    },

    onDragEnd() {
      // Handle drag end if needed
    },

    async loadCompetitions() {
      this.competitionsLoaded = false;
      this.hasCompetitions = false;
      
      try {
        const storedCompetitions = this.getActiveChallenges;
        if (storedCompetitions && Array.isArray(storedCompetitions) && storedCompetitions.length > 0) {
          this.competitions = storedCompetitions;
          this.hasCompetitions = true;
          this.competitionsLoaded = true;
          console.log('Loaded competitions from store:', this.competitions);
          return;
        }

        console.log('Fetching competitions from API...');
        const response = await this.fetchMyActiveChallenges();
        console.log('API Response:', response);
        
        if (response && response.success === false) {
          console.log('No competitions available:', response.message);
          this.competitions = [];
          this.hasCompetitions = false;
          this.competitionsLoaded = true;
          return;
        }
        
        let competitionsData = [];
        if (response && response.data) {
          competitionsData = response.data;
        } else if (response && Array.isArray(response)) {
          competitionsData = response;
        } else if (response && response.competitions) {
          competitionsData = response.competitions;
        }
        
        if (competitionsData && Array.isArray(competitionsData) && competitionsData.length > 0) {
          this.competitions = competitionsData;
          this.hasCompetitions = true;
        } else {
          this.competitions = [];
          this.hasCompetitions = false;
        }
        
        this.competitionsLoaded = true;
        console.log('Final competitions state:', {
          competitions: this.competitions,
          hasCompetitions: this.hasCompetitions,
          loaded: this.competitionsLoaded
        });
        
      } catch (error) {
        console.error('Error loading competitions:', error);
        this.competitions = [];
        this.hasCompetitions = false;
        this.competitionsLoaded = true;
        
        const storedCompetitions = this.getActiveChallenges;
        if (storedCompetitions && Array.isArray(storedCompetitions) && storedCompetitions.length > 0) {
          this.competitions = storedCompetitions;
          this.hasCompetitions = true;
        }
      }
    },

    loadCompetitionStatus() {
      if (this.profile.type === "Agency") {
        this.showRadios = true;
      } else if (["Volunteer", "Company"].includes(this.profile.type)) {
        this.volntrRadios = true;
      }
    }
  },

  watch: {
    dialog(newVal) {
      if (newVal) {
        this.hasCompetitions = false;
        this.competitionsLoaded = false;
        this.selectedCompetition = null;
        this.loadCompetitions();
      }
    },
    initalType(newVal) {
      if (newVal === "media") this.onMediaPick();
    },
    'getActiveChallenges': {
      handler(newVal) {
        if (newVal && Array.isArray(newVal) && newVal.length > 0) {
          this.competitions = newVal;
          this.hasCompetitions = true;
          this.competitionsLoaded = true;
          console.log('Updated competitions from getter:', this.competitions);
        } else if (newVal && Array.isArray(newVal) && newVal.length === 0) {
          this.hasCompetitions = false;
          this.competitionsLoaded = true;
        }
      },
      deep: true,
      immediate: true
    },
    hasCompetitions: {
      handler(newVal) {
        if (!newVal && this.selectedPostType === "Competition") {
          this.selectedPostType = "Individual";
          this.selectedCompetition = null;
        }
      }
    }
  },

  mounted() {
    this.loadCompetitionStatus();
    this.loadCompetitions();
  },

  beforeDestroy() {
    this.resetForm();
  }
};
</script>

<style scoped lang="scss">
// Modern Variables
$primary-gradient: linear-gradient(135deg, #084468 0%, #1e658d 100%);
$success-gradient: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
$card-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
$hover-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
$border-radius: 16px;
$transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

// Dialog Card
.modern-card {
 border-radius: $border-radius !important;
 box-shadow: $card-shadow !important;
 overflow: hidden;
 backdrop-filter: blur(10px);
 background: rgba(255, 255, 255, 0.95) !important;
  
 // Header Enhancement
 .dialog-header {
   background: $primary-gradient;
   color: white;
   padding: 24px;
   position: relative;
  
   &::after {
     content: '';
     position: absolute;
     bottom: 0;
     left: 0;
     right: 0;
     height: 1px;
     background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
   }
  
   .header-content {
     display: flex;
     align-items: center;
     justify-content: space-between;
     width: 100%;
   }
  
   .header-main {
     display: flex;
     align-items: center;
   }
  
   .header-icon-wrapper {
     width: 48px;
     height: 48px;
     border-radius: 12px;
     background: rgba(255, 255, 255, 0.2);
     display: flex;
     align-items: center;
     justify-content: center;
     margin-right: 16px;
     backdrop-filter: blur(10px);
   }
  
   .header-text {
     .dialog-title {
       font-size: 1.5rem;
       font-weight: 600;
       margin: 0;
       letter-spacing: -0.025em;
     }
    
     .dialog-subtitle {
       font-size: 0.875rem;
       opacity: 0.9;
       margin: 4px 0 0 0;
       font-weight: 400;
     }
   }
  
   .modern-close {
     background: rgba(255, 255, 255, 0.1) !important;
     backdrop-filter: blur(10px);
     transition: $transition;
    
     &:hover {
       background: rgba(255, 255, 255, 0.2) !important;
       transform: rotate(90deg);
     }
   }
 }
  
 // Body Enhancement
 .modern-body {
   padding: 32px 24px;
   background: linear-gradient(180deg, #fafbfc 0%, #ffffff 100%);
  
   // User Profile Section
   .user-profile-section {
     margin-bottom: 32px;
    
     .user-profile-card {
       background: white;
       border-radius: 16px;
       padding: 20px;
       box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
       display: flex;
       align-items: center;
       transition: $transition;
       border: 1px solid rgba(0, 0, 0, 0.05);
      
       &:hover {
         transform: translateY(-2px);
         box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
       }
      
       .avatar-wrapper {
         position: relative;
         margin-right: 16px;
        
         .online-indicator {
           position: absolute;
           bottom: 2px;
           right: 2px;
           width: 14px;
           height: 14px;
           background: #4caf50;
           border: 2px solid white;
           border-radius: 50%;
           animation: pulse 2s infinite;
         }
       }
      
       .user-info {
         .user-name {
           font-size: 1.125rem;
           font-weight: 600;
           margin: 0 0 4px 0;
           color: #1a202c;
         }
        
         .user-status {
           font-size: 0.875rem;
           color: #718096;
           margin: 0;
         }
       }
     }
   }
  
   // Section Headers
   .section-header {
     margin-bottom: 20px;
    
     .section-title {
       font-size: 1.125rem;
       font-weight: 600;
       color: #2d3748;
       margin: 0 0 4px 0;
     }
    
     .section-description {
       font-size: 0.875rem;
       color: #718096;
       margin: 0;
     }
   }
  
   // Post Type Grid
   .post-type-grid {
     display: grid;
     grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
     gap: 16px;
     margin-bottom: 24px;
   }
  
   .post-type-card {
     position: relative;
     background: white;
     border: 2px solid #e2e8f0;
     border-radius: 16px;
     padding: 20px;
     cursor: pointer;
     transition: $transition;
     overflow: hidden;
     min-height: 120px;
    
     .card-background {
       position: absolute;
       top: 0;
       left: 0;
       right: 0;
       bottom: 0;
       background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
       opacity: 0;
       transition: $transition;
     }
    
     .card-content {
       position: relative;
       z-index: 2;
       display: flex;
       align-items: flex-start;
       height: 100%;
     }
    
     .icon-container {
       width: 56px;
       height: 56px;
       border-radius: 14px;
       background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%);
       display: flex;
       align-items: center;
       justify-content: center;
       margin-right: 16px;
       transition: $transition;
      
       .type-icon {
         color: #4a5568;
         transition: $transition;
       }
     }
    
     .type-info {
       flex: 1;
      
       .type-title {
         font-size: 1rem;
         font-weight: 600;
         color: #2d3748;
         margin: 0 0 8px 0;
       }
      
       .type-description {
         font-size: 0.875rem;
         color: #718096;
         margin: 0;
         line-height: 1.4;
       }
     }
    
     .selection-indicator {
       position: absolute;
       top: 16px;
       right: 16px;
       opacity: 0;
       transform: scale(0.8);
       transition: $transition;
      
       .check-icon {
         color: #48bb78;
       }
     }
    
     &:hover {
       border-color: #cbd5e0;
       transform: translateY(-4px);
       box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
      
       .card-background {
         opacity: 1;
       }
      
       .icon-container {
         transform: scale(1.1);
       }
     }
    
     &.active {
       border-color: #084468;
       background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%);
      
       .card-background {
         opacity: 1;
         background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
       }
      
       .icon-container {
         background: $primary-gradient;
         transform: scale(1.1);
        
         .type-icon {
           color: white;
         }
       }
      
       .type-title {
         color: #084468;
       }
      
       .selection-indicator {
         opacity: 1;
         transform: scale(1);
       }
     }
   }
  
   // Competition Selector
   .competition-selector {
     margin-top: 16px;
    
     .modern-select {
       ::v-deep .v-input__slot {
         border-radius: 12px !important;
         box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
         border: 2px solid #e2e8f0 !important;
        
         &:hover {
           border-color: #cbd5e0 !important;
         }
       }
      
       ::v-deep .v-input--is-focused .v-input__slot {
         border-color: #084468 !important;
       }
     }
   }

   // No Competitions Message
   .no-competitions-message {
     margin-top: 16px;
     
     .modern-alert {
       border-radius: 12px !important;
       background: rgba(66, 153, 225, 0.05) !important;
     }
   }
  
   // Content Section
   .content-section {
     margin-bottom: 24px;
    
     .content-wrapper {
       position: relative;
     }
     
     .modern-textarea {
       ::v-deep .v-input__slot {
         border-radius: 16px !important;
         border: 2px solid #e2e8f0 !important;
         box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
         transition: $transition !important;
        
         &:hover {
           border-color: #cbd5e0 !important;
           box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
         }
       }
      
       ::v-deep .v-input--is-focused .v-input__slot {
         border-color: #084468 !important;
         box-shadow: 0 4px 20px rgba(102, 126, 234, 0.2) !important;
       }
      
       ::v-deep .v-text-field__slot {
         padding: 16px !important;
       }
      
       .content-icon {
         color: #a0aec0;
         margin-right: 8px;
       }
     }
     
     // Character Counter
     .character-counter {
       display: flex;
       justify-content: space-between;
       align-items: center;
       margin-top: 8px;
       padding: 4px 8px;
       
       span {
         font-size: 0.75rem;
         color: #718096;
         font-weight: 500;
         
         &.text-error {
           color: #e53e3e;
         }
       }
       
       .min-char-warning {
         color: #ed8936;
         display: flex;
         align-items: center;
         gap: 4px;
       }
     }
   }
  
   // Media Section
   .media-section {
     .media-header {
       display: flex;
       align-items: center;
       justify-content: space-between;
       margin-bottom: 16px;
      
       .media-title {
         display: flex;
         align-items: center;
         font-size: 1rem;
         font-weight: 600;
         color: #2d3748;
         margin: 0;
       }
      
       .drag-hint {
         display: flex;
         align-items: center;
         font-size: 0.75rem;
         color: #084468;
         font-weight: 500;
       }
     }
    
     .modern-grid {
       display: grid;
       gap: 16px;
      
       &.single-item {
         grid-template-columns: 1fr;
         max-width: 400px;
       }
      
       &.multiple-items {
         grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
       }
     }
    
     .modern-media {
       position: relative;
       background: white;
       border-radius: 16px;
       overflow: hidden;
       aspect-ratio: 1;
       box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
       transition: $transition;
       border: 2px solid #f7fafc;
      
       &:hover {
         transform: translateY(-4px) scale(1.02);
         box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
         border-color: #084468;
        
         .media-overlay {
           opacity: 1;
         }
        
         .modern-remove {
           opacity: 1;
           transform: scale(1);
         }
       }
      
       .media-preview {
         width: 100%;
         height: 100%;
         position: relative;
        
         .media-image {
           width: 100%;
           height: 100%;
           object-fit: cover;
           transition: $transition;
          
           &:hover {
             transform: scale(1.05);
           }
         }
        
         .image-placeholder {
           display: flex;
           align-items: center;
           justify-content: center;
           height: 100%;
           background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
           color: #a0aec0;
         }
        
         .video-container {
           position: relative;
           width: 100%;
           height: 100%;
          
           .media-video {
             width: 100%;
             height: 100%;
             object-fit: cover;
           }
          
           .video-overlay {
             position: absolute;
             top: 50%;
             left: 50%;
             transform: translate(-50%, -50%);
             opacity: 0;
             transition: $transition;
            
             .play-icon {
               font-size: 48px;
               color: white;
               filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3));
             }
           }
          
           .video-duration {
             position: absolute;
             bottom: 12px;
             right: 12px;
             background: rgba(0, 0, 0, 0.8);
             color: white;
             padding: 4px 8px;
             border-radius: 6px;
             font-size: 0.75rem;
             font-weight: 500;
             backdrop-filter: blur(10px);
           }
         }
       }
      
       .modern-remove {
         position: absolute;
         top: 12px;
         right: 12px;
         background: rgba(239, 68, 68, 0.9) !important;
         color: white !important;
         opacity: 0;
         transform: scale(0.8);
         transition: $transition;
         backdrop-filter: blur(10px);
        
         &:hover {
           background: #dc2626 !important;
           transform: scale(1.1) !important;
         }
       }
      
       .media-overlay {
         position: absolute;
         bottom: 12px;
         left: 12px;
         background: rgba(255, 255, 255, 0.9);
         border-radius: 8px;
         padding: 8px;
         opacity: 0;
         transition: $transition;
         backdrop-filter: blur(10px);
        
         .expand-icon {
           color: #4a5568;
           font-size: 18px;
         }
       }
     }
   }
 }
  
 // Actions Enhancement
 .modern-actions {
   background: linear-gradient(180deg, #ffffff 0%, #f7fafc 100%);
   padding: 20px 24px;
   border-top: 1px solid rgba(0, 0, 0, 0.05);
  
   .action-group {
     display: flex;
     gap: 8px;
    
     .action-btn {
       width: 48px;
       height: 48px;
       border-radius: 12px;
       transition: $transition;
       background: rgba(102, 126, 234, 0.1) !important;
      
       .v-icon {
         color: #084468;
         font-size: 20px;
       }
      
       &:hover {
         background: rgba(102, 126, 234, 0.2) !important;
         transform: translateY(-2px);
         box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
       }
      
       &.media-btn:hover .v-icon {
         color: #e53e3e;
       }
      
       &.emoji-btn:hover .v-icon {
         color: #f6ad55;
       }
     }
   }
  
   .modern-post-btn {
     background: $primary-gradient !important;
     color: white !important;
     border-radius: 12px !important;
     height: 48px !important;
     min-width: 120px !important;
     font-weight: 600 !important;
     text-transform: none !important;
     letter-spacing: 0.025em !important;
     transition: $transition !important;
     position: relative;
     overflow: hidden;
    
     &::before {
       content: '';
       position: absolute;
       top: 0;
       left: 0;
       right: 0;
       bottom: 0;
       background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, transparent 100%);
       opacity: 0;
       transition: $transition;
     }
    
     &:hover:not(:disabled) {
       transform: translateY(-2px);
       box-shadow: 0 12px 40px rgba(102, 126, 234, 0.4) !important;
      
       &::before {
         opacity: 1;
       }
      
       .send-icon {
         transform: translateX(4px);
       }
     }
    
     &:disabled {
       background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e0 100%) !important;
       color: #a0aec0 !important;
       transform: none !important;
       box-shadow: none !important;
       cursor: not-allowed;
     }
    
     .btn-text {
       margin-right: 8px;
     }
    
     .send-icon {
       transition: $transition;
     }
   }
 }
}

// Enhanced Snackbar
.modern-snackbar {
 ::v-deep .v-snack__wrapper {
   border-radius: 12px !important;
   backdrop-filter: blur(10px);
  
   .snackbar-content {
     display: flex;
     align-items: center;
   }
  
   .snackbar-action {
     font-weight: 600;
     text-transform: none;
   }
 }
}

// Emoji Menu
.emoji-menu {
 border-radius: 16px !important;
 overflow: hidden;
 backdrop-filter: blur(10px);
}

// Animations
@keyframes pulse {
 0% {
   box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
 }
 70% {
   box-shadow: 0 0 0 10px rgba(76, 175, 80, 0);
 }
 100% {
   box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
 }
}

// Transitions
.fade-slide-enter-active,
.fade-slide-leave-active {
 transition: all 0.3s ease;
}

.fade-slide-enter,
.fade-slide-leave-to {
 opacity: 0;
 transform: translateY(-10px);
}

.media-transition-enter-active,
.media-transition-leave-active {
 transition: all 0.4s ease;
}

.media-transition-enter,
.media-transition-leave-to {
 opacity: 0;
 transform: translateY(20px) scale(0.95);
}

.animate-in {
 animation: slideInUp 0.5s ease forwards;
}

@keyframes slideInUp {
 from {
   opacity: 0;
   transform: translateY(30px) scale(0.95);
 }
 to {
   opacity: 1;
   transform: translateY(0) scale(1);
 }
}

// Responsive Design
@media (max-width: 768px) {
 .modern-card {
   .dialog-header {
     padding: 20px 16px;
    
     .header-icon-wrapper {
       width: 40px;
       height: 40px;
       margin-right: 12px;
     }
    
     .dialog-title {
       font-size: 1.25rem;
     }
   }
  
   .modern-body {
     padding: 24px 16px;
    
     .post-type-grid {
       grid-template-columns: 1fr;
       gap: 12px;
     }
    
     .post-type-card {
       padding: 16px;
       min-height: 100px;
      
       .icon-container {
         width: 48px;
         height: 48px;
         margin-right: 12px;
       }
      
       .type-title {
         font-size: 0.9375rem;
       }
      
       .type-description {
         font-size: 0.8125rem;
       }
     }
    
     .modern-grid {
       &.multiple-items {
         grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
       }
     }
   }
  
   .modern-actions {
     padding: 16px;
    
     .action-btn {
       width: 44px;
       height: 44px;
     }
    
     .modern-post-btn {
       height: 44px !important;
       min-width: 100px !important;
       font-size: 0.875rem !important;
     }
   }
 }
}

@media (max-width: 480px) {
 .modern-card {
   .modern-body {
     .user-profile-card {
       padding: 16px;
       flex-direction: column;
       text-align: center;
      
       .avatar-wrapper {
         margin-right: 0;
         margin-bottom: 12px;
       }
     }
    
     .post-type-card {
       .card-content {
         flex-direction: column;
         text-align: center;
       }
      
       .icon-container {
         margin-right: 0;
         margin-bottom: 12px;
       }
     }
   }
 }
}

.emoji-picker {
  pointer-events: auto;
}

.emoji-menu {
  pointer-events: auto;
}
</style>